// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { pollStrings } from './strings';
import { POLL_OPTION_MAX_LEN, type PollOption, type PollState } from './types';

export type PollOptionRowMode =
  | { readonly kind: 'composer'; readonly removable: boolean }
  | {
      readonly kind: 'card';
      readonly state: PollState;
      readonly fraction: number;
      readonly isWinner: boolean;
    }
  | { readonly kind: 'static' };

export interface PollOptionRowProps {
  readonly index: number;
  readonly text: string;
  readonly mode: PollOptionRowMode;
  readonly votedByMe?: boolean;
  readonly voteCount?: number;
  readonly onChangeText?: ((text: string) => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
  readonly onPress?: (() => void) | undefined;
}

/**
 * Single poll option. Three layout modes share the chrome so the composer
 * + chat-card + results sheet stay visually consistent:
 *   - `composer`  — text input row with optional remove button
 *   - `card`      — pressable bar with progress fill + vote chip
 *   - `static`    — read-only label (used by results sheet)
 */
export const PollOptionRow = React.memo<PollOptionRowProps>(
  ({ index, text, mode, votedByMe = false, voteCount = 0, onChangeText, onRemove, onPress }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

    const handleChange = useCallback(
      (next: string) => {
        onChangeText?.(next);
      },
      [onChangeText],
    );

    if (mode.kind === 'composer') {
      return (
        <View style={[styles.row, { backgroundColor: theme.colors.surface.secondary }]}>
          <TextInput
            value={text}
            placeholder={pollStrings.optionPlaceholder(index + 1)}
            placeholderTextColor={theme.colors.text.tertiary}
            onChangeText={handleChange}
            maxLength={POLL_OPTION_MAX_LEN}
            style={[styles.input, { color: theme.colors.text.primary }]}
            accessibilityLabel={pollStrings.optionPlaceholder(index + 1)}
          />
          {mode.removable && onRemove !== undefined && (
            <Pressable
              onPress={onRemove}
              accessibilityRole="button"
              accessibilityLabel={pollStrings.removeOption}
              style={({ pressed }) => [styles.removeButton, pressed && { opacity: 0.6 }]}
            >
              <Text style={[styles.removeText, { color: theme.colors.text.secondary }]}>×</Text>
            </Pressable>
          )}
        </View>
      );
    }

    if (mode.kind === 'card') {
      const fillWidth = `${Math.round(mode.fraction * 100)}%` as const;
      const isOpen = mode.state === 'open';
      return (
        <Pressable
          onPress={isOpen ? onPress : undefined}
          accessibilityRole="button"
          accessibilityState={{ selected: votedByMe, disabled: !isOpen }}
          accessibilityLabel={text}
          style={({ pressed }) => [
            styles.cardRow,
            {
              borderColor: votedByMe ? theme.colors.accent.indigo : theme.colors.border.secondary,
              backgroundColor: theme.colors.surface.secondary,
              opacity: pressed && isOpen ? 0.85 : 1,
            },
          ]}
        >
          <View
            style={[
              styles.cardFill,
              {
                width: fillWidth,
                backgroundColor: votedByMe
                  ? `${theme.colors.accent.indigo}33`
                  : `${theme.colors.accent.slate}22`,
              },
            ]}
            accessibilityElementsHidden
          />
          <View style={styles.cardForeground}>
            {mode.isWinner && (
              <Text style={styles.trophy} accessibilityElementsHidden>
                ★
              </Text>
            )}
            <Text style={[styles.cardText, { color: theme.colors.text.primary }]}>{text}</Text>
            <Text style={[styles.cardCount, { color: theme.colors.text.secondary }]}>
              {voteCount}
            </Text>
          </View>
        </Pressable>
      );
    }

    return (
      <View style={[styles.staticRow, { backgroundColor: theme.colors.surface.secondary }]}>
        <Text style={[styles.cardText, { color: theme.colors.text.primary }]}>{text}</Text>
        <Text style={[styles.cardCount, { color: theme.colors.text.secondary }]}>{voteCount}</Text>
      </View>
    );
  },
);

PollOptionRow.displayName = 'PollOptionRow';

/** Convenience type guard used when the host has a typed {@link PollOption}. */
export function pollOptionToCardProps(
  option: PollOption,
  index: number,
  state: PollState,
  fraction: number,
  isWinner: boolean,
): PollOptionRowProps {
  return {
    index,
    text: option.text,
    mode: { kind: 'card', state, fraction, isWinner },
    voteCount: option.voteCount,
    votedByMe: option.votedByMe,
  };
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    borderRadius: 8,
  } satisfies ViewStyle,
  input: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 4,
  } satisfies TextStyle,
  removeButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
  } satisfies ViewStyle,
  removeText: {
    fontSize: 18,
    lineHeight: 18,
  } satisfies TextStyle,
  cardRow: {
    position: 'relative',
    overflow: 'hidden',
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 6,
    minHeight: 38,
    justifyContent: 'center',
  } satisfies ViewStyle,
  cardFill: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
  } satisfies ViewStyle,
  cardForeground: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
  } satisfies ViewStyle,
  trophy: {
    marginRight: 6,
    fontSize: 14,
  } satisfies TextStyle,
  cardText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  } satisfies TextStyle,
  cardCount: {
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 8,
  } satisfies TextStyle,
  staticRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 6,
    borderRadius: 8,
  } satisfies ViewStyle,
});
